import type { Extension, Patient, Reference } from "@medplum/fhirtypes";

/**
 * Patient removal ("tombstone") state — the §5.9 tombstone precondition (ENG-4822).
 *
 * A hard-deleted Patient never surfaces in a `_lastUpdated` search, so a partner
 * mirroring the roster via incremental sync would silently keep a record we
 * removed. Every partner-observable removal must therefore be a status
 * transition, not a delete: the record stays searchable and the roster feed
 * renders it as a tombstone. Removal state lives on the Patient itself so the
 * feed needs no side lookup:
 *
 * - `active: false` plus the {@link PatientRemovalExtension.Reason} extension
 *   mark the record removed. The reason extension is the discriminator —
 *   `active: false` alone is app-level deactivation, not removal.
 * - {@link PatientRemovalExtension.SupersededBy} points at the surviving
 *   Patient when the removal is a merge.
 *
 * Keeping removed records out of normal reads is the read layer's job: point
 * reads resolve removal explicitly ({@link getPatientRemoval}), list reads
 * filter with {@link isPatientRemoved}.
 */
export const PatientRemovalExtension = {
  /** Machine-readable removal reason ({@link PatientRemovalReason}), as `valueString`. */
  Reason: "vibrant.patient.removal-reason",
  /** For merges: `valueReference` to the Patient that supersedes this one. */
  SupersededBy: "vibrant.patient.superseded-by",
} as const;

/**
 * Machine-readable removal reason codes. Partner-facing via the roster feed's
 * tombstone `reason` field — treat values as contract: add codes, never rename.
 */
export const PatientRemovalReason = {
  /** An approved (partner-visible) migration dataset was rejected after the fact. */
  DatasetRejected: "dataset-rejected",
  /** An operator removed the member (support-driven account removal). */
  RemovedByOperator: "removed-by-operator",
  /** A migration rolled back a patient it had just created outside quarantine. */
  MigrationRollback: "migration-rollback",
  /** The patient was merged into another record; `supersededBy` carries the survivor. */
  Merged: "merged",
} as const;

export type PatientRemovalReason = (typeof PatientRemovalReason)[keyof typeof PatientRemovalReason];

export interface PatientRemoval {
  reason: PatientRemovalReason;
  /** The surviving Patient, when the removal is a merge. */
  supersededBy?: Reference<Patient>;
}

/**
 * Removal state read back off a Patient. Looser than {@link PatientRemoval}:
 * server data may predate/outlive the code's reason union, and a stored
 * reference is only `Reference<Resource>` to a reader.
 */
export interface PatientRemovalState {
  reason: string;
  supersededBy?: Reference;
}

/**
 * Extension patch entry shaped for `FhirResource.patchResource`, whose
 * extension merge treats `null` as "delete this key" (structurally compatible
 * with the model layer's `NullableExtension`).
 */
export interface PatientRemovalExtensionPatchEntry {
  url: string;
  valueString?: string | null;
  valueReference?: Reference<Patient> | null;
}

/** The removal extension entries themselves — single source of the write shape. */
function removalExtensionEntries(removal: PatientRemoval): Extension[] {
  return [
    { url: PatientRemovalExtension.Reason, valueString: removal.reason },
    ...(removal.supersededBy
      ? [
          {
            url: PatientRemovalExtension.SupersededBy,
            valueReference: removal.supersededBy,
          },
        ]
      : []),
  ];
}

/**
 * Returns a copy of the patient marked removed: `active: false` plus the
 * removal extensions. Pure — callers persist the result (PUT path). Replaces
 * any prior removal extensions, so re-applying is idempotent and a reason
 * change wins.
 */
export function applyPatientRemoval(patient: Patient, removal: PatientRemoval): Patient {
  const extension: Extension[] = [
    ...withoutRemovalExtensions(patient.extension),
    ...removalExtensionEntries(removal),
  ];
  return { ...patient, active: false, extension };
}

/**
 * Removal extensions shaped for the `patchResource` write path. Replaces prior
 * removal state: an absent `supersededBy` clears any stale merge target left
 * by an earlier removal.
 */
export function buildPatientRemovalExtensionsPatch(
  removal: PatientRemoval,
): PatientRemovalExtensionPatchEntry[] {
  return [
    { url: PatientRemovalExtension.Reason, valueString: removal.reason },
    removal.supersededBy
      ? { url: PatientRemovalExtension.SupersededBy, valueReference: removal.supersededBy }
      : { url: PatientRemovalExtension.SupersededBy, valueReference: null },
  ];
}

/** Patch entries that delete both removal extensions (the tombstone reversal). */
export function buildPatientRemovalClearExtensionsPatch(): PatientRemovalExtensionPatchEntry[] {
  return [
    { url: PatientRemovalExtension.Reason, valueString: null },
    { url: PatientRemovalExtension.SupersededBy, valueReference: null },
  ];
}

/**
 * Resolves the patient's removal state, or `undefined` when the patient is not
 * removed. Point reads use this to resolve removal explicitly instead of
 * treating removed records as missing.
 */
export function getPatientRemoval(patient: Patient): PatientRemovalState | undefined {
  const reason = patient.extension?.find(
    (ext) => ext.url === PatientRemovalExtension.Reason,
  )?.valueString;
  if (!reason) return undefined;

  const supersededBy = patient.extension?.find(
    (ext) => ext.url === PatientRemovalExtension.SupersededBy,
  )?.valueReference;
  return { reason, ...(supersededBy ? { supersededBy } : {}) };
}

/**
 * List-read predicate: true when the patient carries removal state. Roster-style
 * list reads must exclude these records (§5.9); point reads resolve them via
 * {@link getPatientRemoval} instead.
 */
export function isPatientRemoved(patient: Patient): boolean {
  return !!getPatientRemoval(patient);
}

function withoutRemovalExtensions(extensions: Extension[] | undefined): Extension[] {
  const removalUrls: string[] = [
    PatientRemovalExtension.Reason,
    PatientRemovalExtension.SupersededBy,
  ];
  return (extensions ?? []).filter((ext) => !removalUrls.includes(ext.url));
}
